import { useRef, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import ObsidianGate from "@/components/three/ObsidianGate";
import { useAuth } from "@/context/AuthContext";

const ease = [0.22, 1, 0.36, 1];

export const Gate = () => {
  const navigate = useNavigate();
  const { enterWithSigil, member } = useAuth();
  const mouse = useRef({ x: 0, y: 0 });
  const [sigil, setSigil] = useState("");
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const handle = (e) => {
      mouse.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      };
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  useEffect(() => {
    if (member && member.id) navigate("/sanctum");
  }, [member, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sigil.trim()) return;
    setLoading(true);
    setDenied(false);
    try {
      await enterWithSigil(sigil.trim());
      toast.success("The gate stirs. Enter.");
      navigate("/sanctum");
    } catch (err) {
      setDenied(true);
      toast.error(err?.response?.data?.detail || "The gate does not know this Sigil.");
      setTimeout(() => setDenied(false), 600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[#030303]" data-testid="gate-page">
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 8], fov: 45 }} dpr={[1, 2]} gl={{ alpha: true }}>
          <ObsidianGate mouse={mouse} />
        </Canvas>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,#030303_78%)]" />

      <Link
        to="/veil"
        className="absolute left-6 top-6 z-20 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-[#C5A059] md:left-10 md:top-10"
        data-testid="gate-back-link"
      >
        <ArrowLeft size={15} strokeWidth={1.5} /> Return to the Veil
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease }}
        className="relative z-10 flex w-full max-w-md flex-col items-center px-6 text-center"
      >
        <p className="overline mb-5">The Veil Parts Here</p>
        <h1 className="font-serif text-[clamp(2.4rem,7vw,4rem)] font-light leading-[1] tracking-tight text-white">
          Enter Your <span className="italic text-[#C5A059]">Sigil</span>
        </h1>
        <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-white/50">
          Only those who carry the mark may pass into the Inner Sanctum.
        </p>

        <motion.form
          onSubmit={submit}
          animate={denied ? { x: [0, -10, 10, -8, 8, 0] } : {}}
          transition={{ duration: 0.5 }}
          className="mt-10 w-full"
        >
          <input
            value={sigil}
            onChange={(e) => setSigil(e.target.value)}
            placeholder="SYBILS-••••••-••••••••"
            className={`w-full rounded-full border bg-black/40 px-6 py-4 text-center font-mono text-sm tracking-[0.3em] text-[#C5A059] placeholder:text-white/20 outline-none backdrop-blur-md transition-colors duration-300 ${
              denied ? "border-[#7a2222]" : "border-[#C5A059]/40 focus:border-[#C5A059]"
            }`}
            data-testid="gate-sigil-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading}
            className="cta-glow mt-5 w-full rounded-full bg-[#C5A059] py-4 text-xs font-medium uppercase tracking-[0.26em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="gate-submit-btn"
          >
            {loading ? "The gate listens…" : "Present the Sigil"}
          </button>
        </motion.form>

        <button
          onClick={() => navigate("/mysteries")}
          className="mt-8 text-xs uppercase tracking-[0.24em] text-white/40 underline decoration-[#C5A059]/40 underline-offset-8 transition-colors hover:text-[#C5A059]"
          data-testid="gate-request-audience-link"
        >
          I have no Sigil — Request an Audience
        </button>
      </motion.div>
    </div>
  );
};

export default Gate;
